import { useParams } from 'react-router-dom';
import {
  useGetProjectByIdQuery,
  useLazyGetZendeskConsentLinkQuery,
  useZendeskChatIntegrationAutoSetupMutation,
} from 'src/services';
import { FormProvider, useForm } from 'react-hook-form';
import { formResolver } from 'src/utils/formResolver';
import { object } from 'yup';
import { booleanScheme, numberScheme, stringScheme } from 'src/utils/validation-schemes';
import { getSubdomainHelper } from 'src/helpers/getSubdomainHelper';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from 'src/components/ui/accordion';
import { clsx } from 'clsx';
import React from 'react';
import { EditZendeskChatWidgetSettings } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/EditZendeskChatIntegrationSettings/EditZendeskChatWidgetSettings/EditZendeskChatWidgetSettings';
import { EditAutocloseAdvancedSettings } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/EditAutocloseAdvancedSettings/EditAutocloseAdvancedSettings';
import { EditZendeskChatApiDetailsSettings } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/EditZendeskChatIntegrationSettings/EditZendeskChatApiDetailsSettings/EditZendeskChatApiDetailsSettings';
import { setSelectedIntegration } from 'src/store/slices/integrationSlice';
import { useAppDispatch } from 'src/store';
import { EditSettingsAccordionIcon } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/EditSettingsAccordionIcon';
import type { SelectedIntegration } from 'src/store/slices/integrationSlice';
import type { IntegrationSubmitHandler } from 'src/components/Integrations/integrationFormTypes';
import { useProjectAutoclose } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/useProjectAutoclose';
import type { AutocloseFormValues } from 'src/components/Integrations/EditIntegrationPopup/EditIntegrationSettings/useProjectAutoclose';
import { useLoaderWhile } from 'src/hooks/useLoaderWhile';
import { useIntegrationAuthorizationPolling } from 'src/components/Integrations/useIntegrationAuthorizationPolling';

interface ZendeskChatSettingsFormValues extends AutocloseFormValues {
  subdomain: string;
  author_display_name: string;
  author_avatar_url: string;
  app_id: string;
  conversation_key_id: string;
  welcome_message: string;
  responder_mode: string;
  flow_builder: boolean;
  widget_integration_id: string;
  conversation_secret_key: string;
}

export const EditZendeskChatIntegrationSettings = ({
  disabled = false,
  integration,
  onSubmit,
}: {
  disabled?: boolean;
  integration?: SelectedIntegration;
  onSubmit?: IntegrationSubmitHandler;
}) => {
  const [accordionValue, setAccordionValue] = React.useState<string[]>([]);
  const { id } = useParams();
  const { data: currentProject } = useGetProjectByIdQuery({ id: id });
  const { autocloseHours, autocloseMinutes, saveAutoclose, isLoadingUpdateProject } =
    useProjectAutoclose(currentProject);
  const [setZendeskChatAutoSetup, { isLoading: isLoadingSetZendeskChatAutoSetup }] =
    useZendeskChatIntegrationAutoSetupMutation();
  const dispatch = useAppDispatch();
  const isAuthorized = integration?.integration_data?.auth_status === 'ok';
  const isZendeskOAuthUsed = integration?.integration_data?.auth_type === 'oauth';
  const [getZendeskConsentLinkQuery] = useLazyGetZendeskConsentLinkQuery();
  const { isCheckingAuthStatus, pollZendeskAuthorization } = useIntegrationAuthorizationPolling(id);

  useLoaderWhile(isLoadingUpdateProject, isLoadingSetZendeskChatAutoSetup);

  const methods = useForm<ZendeskChatSettingsFormValues>({
    defaultValues: {
      subdomain: integration?.integration_data?.subdomain || '',
      author_display_name: integration?.integration_data?.author_display_name || '',
      author_avatar_url: integration?.integration_data?.author_avatar_url || '',
      app_id: integration?.integration_data?.app_id || '',
      conversation_key_id: integration?.integration_data?.conversation_key_id || '',
      welcome_message: integration?.integration_data?.welcome_message || '',
      autocloseEnabled: Boolean(currentProject?.autoclose_time_window_minutes),
      autocloseHours: autocloseHours,
      autocloseMinutes: autocloseMinutes,
      responder_mode: integration?.integration_data?.responder_mode || 'default_responder',
      flow_builder: integration?.integration_data?.responder_mode
        ? integration?.integration_data?.responder_mode !== 'default_responder'
        : false,
      widget_integration_id: '',
      conversation_secret_key: '',
    },
    resolver: formResolver(
      object({
        subdomain: stringScheme({ required: false }).label('Zendesk subdomain'),
        author_display_name: stringScheme({ required: false }).label('Author display name'),
        author_avatar_url: stringScheme({ required: false }).label('Author avatar url').nullable(),
        app_id: stringScheme({ required: false }).label('App ID'),
        conversation_key_id: stringScheme({ required: false }).label('Key ID'),
        conversation_secret_key: stringScheme({ required: false }).label('Secret key'),
        welcome_message: stringScheme({ required: false }).label('AI Welcome message'),
        widget_integration_id: stringScheme({ required: false }).label('Widget integration id'),
        autocloseHours: numberScheme({ required: true }),
        autocloseMinutes: numberScheme({ required: true }),
        responder_mode: stringScheme({ required: true }).label('Responder Mode'),
        flow_builder: booleanScheme({ required: true }),
      }),
    ),
  });

  const openZendeskConsentPage = async () => {
    const { data } = await getZendeskConsentLinkQuery({
      project_id: id,
      integration_id: integration.id,
      type: 'zendesk-chat',
    });
    if (!data?.authorize_url) return false;

    window.open(data.authorize_url);
    return true;
  };

  const onConnectWithZendesk = async () => {
    if (integration?.id) {
      if (await openZendeskConsentPage()) {
        pollZendeskAuthorization(integration.id, (authorizedIntegration) =>
          dispatch(setSelectedIntegration({ ...integration, ...authorizedIntegration })),
        );
      }
    }
  };

  // The integration is already authorized, so there is no status change to poll for.
  const onReconnectWithZendesk = async () => {
    if (integration?.id) {
      await openZendeskConsentPage();
    }
  };

  // Runs the same validation as submit; the connect handlers only fire for a valid form.
  const connectWithZendesk = methods.handleSubmit(onConnectWithZendesk);
  const reconnectWithZendesk = methods.handleSubmit(onReconnectWithZendesk);

  const submit = async (data: ZendeskChatSettingsFormValues) => {
    if (data.widget_integration_id) {
      await setZendeskChatAutoSetup({
        projectId: id,
        integrationId: integration.id,
        body: {
          integrationId: integration.id,
          widget_integration_id: data.widget_integration_id,
          responder_mode: data.flow_builder ? 'handoff' : 'default_responder',
        },
      });
    }
    delete data.widget_integration_id;
    delete data.flow_builder;
    delete data.responder_mode;

    await saveAutoclose(data);
    delete data.autocloseEnabled;
    delete data.autocloseHours;
    delete data.autocloseMinutes;
    delete data.conversation_secret_key;

    onSubmit({
      id: integration.id,
      status: integration.status,
      integration_type: integration.integration_type,
      integration_data: {
        ...integration.integration_data,
        ...data,
        subdomain: getSubdomainHelper(data.subdomain)?.subdomain || data.subdomain,
        project_name: currentProject.name,
      },
    });
  };

  return (
    <FormProvider {...methods}>
      <form
        id="edit-integration-form"
        onSubmit={methods.handleSubmit(submit)}
        className={'max-h-[60vh] overflow-auto pr-4'}
      >
        <Accordion
          value={accordionValue}
          onValueChange={setAccordionValue}
          type="multiple"
          className="w-full"
          disabled={disabled}
        >
          <AccordionItem
            value="web-widget-settings"
            className={clsx('group bg-white border-b-[#A498FF4D]')}
          >
            <AccordionTrigger hideDefaultIcon className="py-5 cursor-pointer">
              <div className="flex row items-center justify-between w-full">
                <div>
                  <p className={'text-[#7A6BE5] font-semibold text-sm'}>Web Widget settings</p>
                </div>
                <div>
                  <EditSettingsAccordionIcon
                    open={accordionValue.includes('web-widget-settings')}
                  />
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-5">
              <EditZendeskChatWidgetSettings integration={integration} />
            </AccordionContent>
          </AccordionItem>
          <AccordionItem
            value="advanced-settings"
            className={clsx('group bg-white border-b-[#A498FF4D]')}
          >
            <AccordionTrigger hideDefaultIcon className="py-5 cursor-pointer">
              <div className="flex row items-center justify-between w-full">
                <div>
                  <p className={'text-[#7A6BE5] font-semibold text-sm'}>Advanced settings</p>
                </div>
                <div>
                  <EditSettingsAccordionIcon open={accordionValue.includes('advanced-settings')} />
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-5">
              <EditAutocloseAdvancedSettings currentProject={currentProject} />
            </AccordionContent>
          </AccordionItem>
          <AccordionItem
            value="api-details"
            className={clsx('group bg-white border-b-[#A498FF4D]')}
          >
            <AccordionTrigger hideDefaultIcon className="py-5 cursor-pointer">
              <div className="flex row items-center justify-between w-full">
                <div>
                  <p className={'text-[#7A6BE5] font-semibold text-sm'}>API details</p>
                </div>
                <div>
                  <EditSettingsAccordionIcon open={accordionValue.includes('api-details')} />
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pb-5">
              <EditZendeskChatApiDetailsSettings
                isAuthorized={isAuthorized}
                isZendeskOAuthUsed={isZendeskOAuthUsed}
                isCheckingAuthStatus={isCheckingAuthStatus}
                onConnectWithZendesk={connectWithZendesk}
                onReconnectWithZendesk={reconnectWithZendesk}
              />
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </form>
    </FormProvider>
  );
};
